import type {
  CertificationEntry,
  EducationEntry,
  ExperienceEntry,
  MasterProfile,
  ProjectEntry,
  SkillEntry,
} from '../../lib/profile/types';
import {
  addContactLink,
  appendEntry,
  blankCertification,
  blankEducation,
  blankExperience,
  blankProject,
  blankSkill,
  census,
  confirmContactField,
  confirmContactLink,
  confirmEntry,
  confirmSummary,
  entryList,
  moveContactLink,
  moveEntry,
  removeContactLink,
  removeEntry,
  setContactField,
  setSummary,
  updateContactLink,
  updateEntry,
} from '../../lib/profile/model';
import { Badge, Button, Card, FactBadge, SectionHeader, Tooltip, controlClass } from '../ui';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, ShieldIcon, TrashIcon } from '../ui/icons';
import {
  validateCertification,
  validateContactField,
  validateContactLink,
  validateEducation,
  validateExperience,
  validateProject,
  validateSkill,
  validateSummary,
} from '../../lib/profile/validate';

interface Props {
  profile: MasterProfile;
  onChange: (next: MasterProfile) => void;
}

function badIssuesText(errors: Record<string, string>): string | null {
  const vals = Object.values(errors);
  return vals.length > 0 ? vals[0] ?? null : null;
}

const CONTACT_LABEL: Record<'fullName' | 'email' | 'phone' | 'location', string> = {
  fullName: 'Full name',
  email: 'Email',
  phone: 'Phone',
  location: 'Location',
};

export function ProfilePanel({ profile, onChange }: Props) {
  const c = census(profile);
  const completion = c.total > 0 ? Math.round((c.confirmed / c.total) * 100) : 0;

  const update = (next: MasterProfile) => onChange(next);

  return (
    <div className="space-y-6">
      {/* Overview */}
      <Card className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="flex h-9 w-9 items-center justify-center rounded-pill bg-accent-soft text-accent-strong"
            >
              <ShieldIcon className="h-4 w-4" />
            </span>
            <h2 className="text-lg font-semibold tracking-tight text-ink">
              Profile completeness
            </h2>
          </div>
          <p className="mt-2 max-w-prose text-sm text-ink-soft">
            Facts are kept only in browser memory. Refreshing or closing this page
            discards unsaved changes. Nothing here is sent anywhere.
          </p>
          <p className="mt-2 text-sm font-medium text-ink" aria-live="polite">
            {c.confirmed} of {c.total} facts confirmed. {c.needsReview} need review,{' '}
            {c.corrected} edited.
          </p>
        </div>

        <div className="flex shrink-0 flex-col items-stretch gap-3 sm:w-64">
          <div className="flex items-end justify-between">
            <span className="text-3xl font-semibold leading-none text-ink">
              {completion}
              <span className="text-base text-ink-faint">%</span>
            </span>
            <span className="text-xs font-medium uppercase tracking-wide text-ink-faint">
              confirmed
            </span>
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-pill bg-line"
            role="progressbar"
            aria-valuenow={completion}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Profile completion ${completion} percent`}
          >
            <div
              className="h-full rounded-pill bg-accent transition-[width] duration-700 ease-out"
              style={{ width: `${completion}%` }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge tone="gap">{c.needsReview} to review</Badge>
            <Badge tone="danger">{c.corrected} edited</Badge>
            <Badge tone="verified">{c.confirmed} confirmed</Badge>
          </div>
        </div>
      </Card>

      {/* Contact */}
      <Card aria-labelledby="contact-heading">
        <SectionHeader
          id="contact-heading"
          title="Contact information"
          description="Confirm each detail before it can appear on a resume."
        />
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {(['fullName', 'email', 'phone', 'location'] as const).map((field) => {
            const tracked = profile.contact[field];
            const issues = validateContactField(field, tracked.value);
            const error = badIssuesText(issues.errors);
            return (
              <div key={field} className="flex flex-col gap-1.5">
                <label
                  className="text-sm font-semibold text-ink"
                  htmlFor={`contact-${field}`}
                >
                  {CONTACT_LABEL[field]}
                </label>
                <input
                  id={`contact-${field}`}
                  type="text"
                  value={tracked.value}
                  onChange={(e) => update(setContactField(profile, field, e.target.value))}
                  className={`w-full rounded-md border bg-surface px-3 py-2 text-sm text-ink transition-colors ${
                    error ? 'border-danger' : 'border-line-strong hover:border-navy/40'
                  }`}
                />
                <div className="flex items-center justify-between gap-2">
                  <FactBadge state={tracked.state} />
                  <Button
                    size="sm"
                    variant="quiet"
                    onClick={() => update(confirmContactField(profile, field))}
                    disabled={tracked.value.trim().length === 0 || !!error}
                  >
                    Confirm
                  </Button>
                </div>
                {error ? (
                  <p role="alert" className="text-xs font-medium text-danger">
                    {error}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="mt-5 space-y-3 border-t border-line pt-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-ink">Links</h4>
            <Button
              size="sm"
              variant="ghost"
              icon={<PlusIcon />}
              onClick={() => update(addContactLink(profile))}
            >
              Add link
            </Button>
          </div>
          {profile.contact.links.length === 0 ? (
            <p className="text-sm text-ink-faint">No links yet.</p>
          ) : null}
          {profile.contact.links.map((link, index) => {
            const issues = validateContactLink(link);
            const error = badIssuesText(issues.errors);
            return (
              <div
                key={link.id}
                className="grid gap-3 rounded-md border border-line bg-surface-sunken p-3 sm:grid-cols-2"
              >
                <div>
                  <label
                    className="text-xs font-medium text-ink-soft"
                    htmlFor={`link-label-${link.id}`}
                  >
                    Label
                  </label>
                  <input
                    id={`link-label-${link.id}`}
                    type="text"
                    value={link.label}
                    onChange={(e) =>
                      update(updateContactLink(profile, link.id, { label: e.target.value }))
                    }
                    className={controlClass}
                  />
                </div>
                <div>
                  <label
                    className="text-xs font-medium text-ink-soft"
                    htmlFor={`link-url-${link.id}`}
                  >
                    URL
                  </label>
                  <input
                    id={`link-url-${link.id}`}
                    type="text"
                    value={link.url}
                    onChange={(e) =>
                      update(updateContactLink(profile, link.id, { url: e.target.value }))
                    }
                    className={controlClass}
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 sm:col-span-2">
                  <FactBadge state={link.state} />
                  <div className="flex items-center gap-2">
                    <Tooltip label="Move link up">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Up"
                        disabled={index === 0}
                        onClick={() => update(moveContactLink(profile, link.id, -1))}
                        icon={<ArrowUpIcon />}
                      />
                    </Tooltip>
                    <Tooltip label="Move link down">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Down"
                        disabled={index === profile.contact.links.length - 1}
                        onClick={() => update(moveContactLink(profile, link.id, 1))}
                        icon={<ArrowDownIcon />}
                      />
                    </Tooltip>
                    <Button
                      size="sm"
                      variant="quiet"
                      onClick={() => update(confirmContactLink(profile, link.id))}
                    >
                      Confirm
                    </Button>
                    <Tooltip label="Remove link">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Remove"
                        onClick={() => update(removeContactLink(profile, link.id))}
                        icon={<TrashIcon />}
                      />
                    </Tooltip>
                  </div>
                </div>
                {error ? (
                  <p role="alert" className="text-xs font-medium text-danger sm:col-span-2">
                    {error}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Summary */}
      <Card aria-labelledby="summary-heading">
        <SectionHeader
          id="summary-heading"
          title="Professional summary"
          action={<FactBadge state={profile.summary.state} />}
        />
        <textarea
          aria-label="Professional summary"
          value={profile.summary.value}
          onChange={(e) => update(setSummary(profile, e.target.value))}
          className={`${controlClass} mt-3 min-h-24 leading-relaxed`}
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-xs text-danger">
            {validateSummary(profile.summary.value).errors.summary ?? ''}
          </span>
          <Button
            size="sm"
            variant="quiet"
            onClick={() => update(confirmSummary(profile))}
            disabled={!!validateSummary(profile.summary.value).errors.summary}
          >
            Confirm
          </Button>
        </div>
      </Card>

      {/* Repeatable sections */}
      {(['experience', 'education', 'skills', 'certifications', 'projects'] as const).map(
        (section) => (
          <RepeatableSection
            key={section}
            section={section}
            profile={profile}
            onChange={update}
          />
        ),
      )}
    </div>
  );
}

function RepeatableSection({
  section,
  profile,
  onChange,
}: {
  section: 'experience' | 'education' | 'skills' | 'certifications' | 'projects';
  profile: MasterProfile;
  onChange: (p: MasterProfile) => void;
}) {
  const titleMap = {
    experience: 'Work experience',
    education: 'Education',
    skills: 'Skills',
    certifications: 'Certifications',
    projects: 'Projects',
  } as const;
  const entries = entryList(profile, section) as Array<
    ExperienceEntry | EducationEntry | SkillEntry | CertificationEntry | ProjectEntry
  >;

  const appendBlank = () => {
    if (section === 'experience') onChange(appendEntry(profile, 'experience', blankExperience()));
    if (section === 'education') onChange(appendEntry(profile, 'education', blankEducation()));
    if (section === 'skills') onChange(appendEntry(profile, 'skills', blankSkill()));
    if (section === 'certifications')
      onChange(appendEntry(profile, 'certifications', blankCertification()));
    if (section === 'projects') onChange(appendEntry(profile, 'projects', blankProject()));
  };

  return (
    <Card role="region" aria-labelledby={`${section}-heading`}>
      <SectionHeader
        id={`${section}-heading`}
        title={titleMap[section]}
        action={
          <Button size="sm" variant="secondary" icon={<PlusIcon />} onClick={appendBlank}>
            Add entry
          </Button>
        }
      />
      <div className="mt-4 space-y-3">
        {entries.length === 0 ? (
          <p className="rounded-md border border-dashed border-line-strong bg-surface-sunken px-4 py-6 text-center text-sm text-ink-faint">
            No entries yet.
          </p>
        ) : null}
        {entries.map((entry, index) => (
          <EntryCard
            key={entry.id}
            section={section}
            profile={profile}
            entry={entry}
            index={index}
            count={entries.length}
            onChange={onChange}
          />
        ))}
      </div>
    </Card>
  );
}

function EntryCard({
  section,
  profile,
  entry,
  index,
  count,
  onChange,
}: {
  section: 'experience' | 'education' | 'skills' | 'certifications' | 'projects';
  profile: MasterProfile;
  entry: ExperienceEntry | EducationEntry | SkillEntry | CertificationEntry | ProjectEntry;
  index: number;
  count: number;
  onChange: (p: MasterProfile) => void;
}) {
  const updateField = (field: string, value: unknown) => {
    const patch: Record<string, unknown> = { [field]: value };
    if (section === 'experience') onChange(updateEntry(profile, 'experience', entry.id, patch as never));
    if (section === 'education') onChange(updateEntry(profile, 'education', entry.id, patch as never));
    if (section === 'skills') onChange(updateEntry(profile, 'skills', entry.id, patch as never));
    if (section === 'certifications')
      onChange(updateEntry(profile, 'certifications', entry.id, patch as never));
    if (section === 'projects') onChange(updateEntry(profile, 'projects', entry.id, patch as never));
  };

  let issues: { errors: Record<string, string> } = { errors: {} };
  if (section === 'experience') issues = validateExperience(entry as ExperienceEntry);
  if (section === 'education') issues = validateEducation(entry as EducationEntry);
  if (section === 'skills') issues = validateSkill(entry as SkillEntry);
  if (section === 'certifications') issues = validateCertification(entry as CertificationEntry);
  if (section === 'projects') issues = validateProject(entry as ProjectEntry);
  const errorText = Object.values(issues.errors)[0] ?? null;

  const confirm = () => onChange(confirmEntry(profile, section, entry.id));
  const remove = () => onChange(removeEntry(profile, section, entry.id));
  const moveUp = () => onChange(moveEntry(profile, section, entry.id, -1));
  const moveDown = () => onChange(moveEntry(profile, section, entry.id, 1));

  const isExperience = section === 'experience';
  const isEducation = section === 'education';
  const isSkill = section === 'skills';
  const isCert = section === 'certifications';
  const isProject = section === 'projects';

  const inputClass = controlClass;

  return (
    <div
      className="rounded-md border border-line bg-surface-sunken p-3 sm:p-4"
      aria-label={`${section} entry ${index + 1}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FactBadge state={entry.state} />
        <div className="flex items-center gap-1.5">
          <Tooltip label="Move entry up">
            <Button
              size="sm"
              variant="ghost"
              aria-label="Up"
              disabled={index === 0}
              onClick={moveUp}
              icon={<ArrowUpIcon />}
            />
          </Tooltip>
          <Tooltip label="Move entry down">
            <Button
              size="sm"
              variant="ghost"
              aria-label="Down"
              disabled={index === count - 1}
              onClick={moveDown}
              icon={<ArrowDownIcon />}
            />
          </Tooltip>
          <Button size="sm" variant="quiet" onClick={confirm} disabled={!!errorText}>
            Confirm
          </Button>
          <Tooltip label="Remove entry">
            <Button
              size="sm"
              variant="ghost"
              aria-label="Remove"
              onClick={remove}
              icon={<TrashIcon />}
            />
          </Tooltip>
        </div>
      </div>

      <div className="mt-3 grid gap-3">
        {isSkill ? (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-ink-soft">Skill</span>
            <input
              type="text"
              value={(entry as SkillEntry).name}
              onChange={(e) => updateField('name', e.target.value)}
              className={inputClass}
            />
          </label>
        ) : null}

        {isCert ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">Name</span>
              <input
                type="text"
                value={(entry as CertificationEntry).name}
                onChange={(e) => updateField('name', e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">Issuer</span>
              <input
                type="text"
                value={(entry as CertificationEntry).issuer}
                onChange={(e) => updateField('issuer', e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">Date</span>
              <input
                type="text"
                value={(entry as CertificationEntry).date}
                onChange={(e) => updateField('date', e.target.value)}
                className={inputClass}
              />
            </label>
          </div>
        ) : null}

        {isProject ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-ink-soft">Name</span>
                <input
                  type="text"
                  value={(entry as ProjectEntry).name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-ink-soft">URL</span>
                <input
                  type="text"
                  value={(entry as ProjectEntry).url}
                  onChange={(e) => updateField('url', e.target.value)}
                  className={inputClass}
                />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">Description</span>
              <textarea
                value={(entry as ProjectEntry).description}
                onChange={(e) => updateField('description', e.target.value)}
                className={inputClass}
              />
            </label>
          </>
        ) : null}

        {isExperience ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-ink-soft">Employer</span>
                <input
                  type="text"
                  value={(entry as ExperienceEntry).organization}
                  onChange={(e) => updateField('organization', e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-ink-soft">Role</span>
                <input
                  type="text"
                  value={(entry as ExperienceEntry).role}
                  onChange={(e) => updateField('role', e.target.value)}
                  className={inputClass}
                />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">Location</span>
              <input
                type="text"
                value={(entry as ExperienceEntry).location}
                onChange={(e) => updateField('location', e.target.value)}
                className={inputClass}
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-ink-soft">Start (YYYY-MM)</span>
                <input
                  type="text"
                  value={(entry as ExperienceEntry).period.start}
                  onChange={(e) =>
                    updateField('period', {
                      ...(entry as ExperienceEntry).period,
                      start: e.target.value,
                    })
                  }
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-ink-soft">End (YYYY-MM)</span>
                <input
                  type="text"
                  value={(entry as ExperienceEntry).period.end}
                  onChange={(e) =>
                    updateField('period', {
                      ...(entry as ExperienceEntry).period,
                      end: e.target.value,
                    })
                  }
                  className={inputClass}
                />
              </label>
              <label className="flex items-center gap-2 pt-5 text-sm">
                <input
                  type="checkbox"
                  checked={(entry as ExperienceEntry).period.current}
                  onChange={(e) =>
                    updateField('period', {
                      ...(entry as ExperienceEntry).period,
                      current: e.target.checked,
                    })
                  }
                  className="h-4 w-4 rounded border-line-strong text-accent"
                />
                <span className="font-medium text-ink-soft">Present</span>
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">Responsibilities (one per line)</span>
              <textarea
                value={(entry as ExperienceEntry).responsibilities.join('\n')}
                onChange={(e) =>
                  updateField(
                    'responsibilities',
                    e.target.value.split('\n').filter((l) => l.trim().length > 0),
                  )
                }
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">Achievements (one per line)</span>
              <textarea
                value={(entry as ExperienceEntry).achievements.join('\n')}
                onChange={(e) =>
                  updateField(
                    'achievements',
                    e.target.value.split('\n').filter((l) => l.trim().length > 0),
                  )
                }
                className={inputClass}
              />
            </label>
          </>
        ) : null}

        {isEducation ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">Institution</span>
              <input
                type="text"
                value={(entry as EducationEntry).institution}
                onChange={(e) => updateField('institution', e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">Credential</span>
              <input
                type="text"
                value={(entry as EducationEntry).credential}
                onChange={(e) => updateField('credential', e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">Field of study</span>
              <input
                type="text"
                value={(entry as EducationEntry).area}
                onChange={(e) => updateField('area', e.target.value)}
                className={inputClass}
              />
            </label>
          </div>
        ) : null}
      </div>

      {errorText ? (
        <p role="alert" className="mt-2 text-xs font-medium text-danger">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}
